import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

import { UserContext } from './User';
import { EntitlementsContext } from './Entitlements';
import { CHECKLIST } from '../onboarding/checklist';

/**
 * Store setup checklist state.
 *
 * Fetches GET /api/onboarding for the active store and exposes the merged
 * view of "what the checklist says" (src/onboarding/checklist.js) and "what
 * this store has actually done" (derived server-side from live data).
 *
 * Two deliberate design rules, both from the onboarding spec:
 *
 *  1. Completion is NEVER stored client-side and never cached across a store
 *     switch. `done` always comes from the server, recomputed per request, so
 *     the widget cannot drift out of sync with the store's real configuration.
 *
 *  2. A store is "new" when it has no listings — there is no first-run flag to
 *     read or maintain. The backend returns `mode`, derived from that count, and
 *     it flips to 'maintenance' by itself the moment the first listing exists.
 *
 * FAIL QUIET, in the opposite direction to Entitlements: if the endpoint is
 * missing (backend not deployed yet), errors, or the store has dismissed the
 * checklist, `visible` is false and nothing renders. A setup nudge is never
 * important enough to risk showing a broken panel over someone's working store.
 */

const defaultValue = {
  loading: true,
  available: false,
  visible: false,
  mode: 'maintenance',
  storeId: null,
  viewerIsOwner: false,
  dismissed: false,
  blocks: [],
  total: 0,
  done: 0,
  requiredRemaining: 0,
  complete: false,
  open: false,
  setOpen: () => {},
  skipItem: () => {},
  dismiss: () => {},
  restore: () => {},
  refresh: () => {},
};

export const OnboardingContext = createContext(defaultValue);

/**
 * Minimum gap between automatic re-reads. Navigation and window focus both
 * trigger one, and a merchant working through the list does a lot of both —
 * this keeps a burst of route changes down to a single request without ever
 * making the panel feel stale.
 */
const REFRESH_INTERVAL_MS = 10000;

export function OnboardingProvider({ children }) {
  const { user } = useContext(UserContext);
  const { featureState } = useContext(EntitlementsContext);
  const location = useLocation();
  const [state, setState] = useState({ loading: true, data: null });
  const [open, setOpen] = useState(false);
  const lastFetchedAt = useRef(0);

  const refresh = useCallback(async () => {
    lastFetchedAt.current = Date.now();
    try {
      const { data } = await axios.get('/api/onboarding');
      setState({ loading: false, data });
    } catch (e) {
      // No endpoint, no permission, no panel. Silent by design.
      setState({ loading: false, data: null });
    }
  }, []);

  /** Re-read unless we just did. Used by the automatic triggers below. */
  const maybeRefresh = useCallback(() => {
    if (Date.now() - lastFetchedAt.current < REFRESH_INTERVAL_MS) return;
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (user?.id) {
      refresh();
    } else {
      setState({ loading: false, data: null });
      setOpen(false);
    }
  }, [user?.id, refresh]);

  /**
   * Items tick themselves. A merchant follows a step to Settings, saves (every
   * field in this app auto-saves), and navigates back — this re-read is what
   * turns the item green without a manual refresh or a "mark as done" button.
   */
  useEffect(() => {
    if (user?.id) maybeRefresh();
  }, [location.pathname, user?.id, maybeRefresh]);

  /**
   * Returning to the tab covers the two cases navigation can't see: coming back
   * from an eBay/Xero OAuth round-trip, and finishing something in another tab.
   */
  useEffect(() => {
    if (!user?.id) return undefined;

    const onFocus = () => maybeRefresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [user?.id, maybeRefresh]);

  // The panel opens on its own in exactly two places, both in OnboardingWidget:
  // arriving on Store Settings, and once per browser session for the store's
  // OWNER while essential steps remain. Everyone else only gets the launcher.
  // Its permanent home is Store Settings.

  /**
   * Optimistic write-through. Both skip and dismiss are one-click, instantly
   * persisted actions — there is no Save step anywhere in this product, so the
   * click IS the commit. On failure we re-read the server rather than trying to
   * unwind, so the panel always ends up showing the truth.
   */
  const persist = useCallback(async (request, optimistic) => {
    setState((prev) => (prev.data ? { ...prev, data: optimistic(prev.data) } : prev));
    try {
      // Both endpoints return the freshly recomputed state, so the optimistic
      // paint is replaced by the server's truth rather than merely confirmed —
      // a skip can change what else is counted.
      const { data } = await request();
      lastFetchedAt.current = Date.now();
      setState({ loading: false, data });
    } catch (e) {
      refresh();
    }
  }, [refresh]);

  const skipItem = useCallback((key, skipped = true) => persist(
    () => axios.post(`/api/onboarding/items/${key}/skip`, { skipped }),
    (data) => ({ ...data, items: { ...data.items, [key]: { ...data.items?.[key], skipped } } }),
  ), [persist]);

  const dismiss = useCallback(() => {
    setOpen(false);
    return persist(
      () => axios.post('/api/onboarding/dismiss', { dismissed: true }),
      (data) => ({ ...data, dismissed: true }),
    );
  }, [persist]);

  const restore = useCallback(() => persist(
    () => axios.post('/api/onboarding/dismiss', { dismissed: false }),
    (data) => ({ ...data, dismissed: false }),
  ), [persist]);

  const value = useMemo(() => {
    const data = state.data;
    const items = data?.items || {};

    // An item the store isn't entitled to is dropped entirely — not shown
    // locked. The checklist must never advertise a feature the store can't use.
    const entitled = (feature) => !feature || featureState(feature) === 'on';

    const blocks = CHECKLIST
      .map((block) => ({
        ...block,
        items: block.items
          .filter((it) => entitled(it.feature))
          .map((it) => ({
            ...it,
            done: items[it.key]?.done === true,
            skipped: items[it.key]?.skipped === true,
          })),
      }))
      .filter((block) => block.items.length > 0);

    // Skipped items drop out of the count because the merchant declined them;
    // pending ones because the product can't do them yet. Neither should hold a
    // store short of 100%.
    const flat = blocks.flatMap((b) => b.items);
    const counted = flat.filter((it) => !it.skipped && !it.pending);
    const done = counted.filter((it) => it.done).length;
    const requiredRemaining = counted.filter((it) => !it.optional && !it.done).length;

    const available = Boolean(data);
    const dismissed = data?.dismissed === true;

    return {
      loading: state.loading,
      available,
      visible: available && !dismissed,
      mode: data?.mode || 'maintenance',
      storeId: data?.store_id ?? null,
      // Server-decided: the store's real owner, never RC master staff.
      viewerIsOwner: data?.viewer_is_owner === true,
      dismissed,
      blocks,
      total: counted.length,
      done,
      requiredRemaining,
      complete: counted.length > 0 && done === counted.length,
      open,
      setOpen,
      skipItem,
      dismiss,
      restore,
      refresh,
    };
  }, [state, open, featureState, skipItem, dismiss, restore, refresh]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

/** const { blocks, done, total, setOpen } = useOnboarding(); */
export function useOnboarding() {
  return useContext(OnboardingContext);
}

export default OnboardingProvider;
